// src/cockpit4/components/KnowledgeFeedback.tsx
// ============================================
// P1-功能5：知识使用反馈闭环（知识中心）
//
// 功能：
//   1. 知识有用/没用 一键反馈（每条知识卡片显示👍👎按钮）
//   2. 使用场景和效果记录（点击反馈时记录使用场景）
//   3. 知识改进建议收集（反馈时可选填改进建议）
//   4. 反馈自动同步给知识负责人（记录到 localStorage，可查看反馈列表）
//   5. 知识迭代版本管理（记录反馈历史，版本号）
//
// 数据来源：
//   - /vault-api/list 获取知识列表（09_Knowledge（知识中心）/Cards）
//   - localStorage 持久化反馈数据
//
// localStorage 键设计：
//   xeaos:knowledge:feedback - 全局反馈数组
//   每条反馈包含：path, title, rating(useful/useless), scene, suggestion, timestamp, version
//   版本号 = 同一 path 的累计反馈数 + 1
// ============================================

import { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ThumbsUp,
  ThumbsDown,
  MessageCircle,
  RefreshCw,
  Loader2,
  X,
  FileText,
  History,
  TrendingUp,
  CheckCircle2,
  Filter,
} from "lucide-react";

// ============================================
// 类型定义
// ============================================

/** 笔记元信息（来自 /vault-api/list） */
interface NoteMeta {
  /** 文件名 */
  name: string;
  /** Vault 相对路径 */
  path: string;
  /** 修改时间 */
  mtime: string;
  /** 文件大小 */
  size: number;
}

/** 反馈评分 */
type FeedbackRating = "useful" | "useless";

/** 单条反馈记录 */
interface FeedbackEntry {
  /** 反馈 ID（时间戳 + 随机） */
  id: string;
  /** 知识卡片 Vault 路径 */
  path: string;
  /** 知识卡片标题 */
  title: string;
  /** 反馈评分 */
  rating: FeedbackRating;
  /** 使用场景描述 */
  scene: string;
  /** 改进建议 */
  suggestion: string;
  /** 反馈时间 ISO */
  timestamp: string;
  /** 该卡片的反馈版本号（同 path 累计 +1） */
  version: number;
}

/** 反馈汇总（按 path 聚合） */
interface FeedbackSummary {
  path: string;
  title: string;
  usefulCount: number;
  uselessCount: number;
  latestVersion: number;
  latestTimestamp: string;
  history: FeedbackEntry[];
}

// ============================================
// 常量
// ============================================

/** localStorage 键 */
const FEEDBACK_STORAGE_KEY = "xeaos:knowledge:feedback";

/** 知识中心 Cards 路径 */
const KNOWLEDGE_CARDS_DIR = "09_Knowledge（知识中心）/Cards";

// ============================================
// 工具函数
// ============================================

/** 从 localStorage 加载全部反馈 */
function loadFeedbacks(): FeedbackEntry[] {
  try {
    const raw = localStorage.getItem(FEEDBACK_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed as FeedbackEntry[];
  } catch {
    return [];
  }
}

/** 保存全部反馈到 localStorage */
function saveFeedbacks(entries: FeedbackEntry[]): void {
  try {
    localStorage.setItem(FEEDBACK_STORAGE_KEY, JSON.stringify(entries));
  } catch (err) {
    console.warn("[KnowledgeFeedback] 保存反馈失败:", err);
  }
}

/** 生成唯一 ID */
function genId(): string {
  return `fb-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

// ============================================
// 主组件
// ============================================

export default function KnowledgeFeedback() {
  // 知识卡片列表
  const [notes, setNotes] = useState<NoteMeta[]>([]);
  // 加载状态
  const [loading, setLoading] = useState(true);
  // 全部反馈记录
  const [feedbacks, setFeedbacks] = useState<FeedbackEntry[]>([]);
  // 当前正在反馈的卡片（Modal）
  const [activeNote, setActiveNote] = useState<NoteMeta | null>(null);
  // 当前反馈评分
  const [activeRating, setActiveRating] = useState<FeedbackRating>("useful");
  // 使用场景输入
  const [sceneInput, setSceneInput] = useState("");
  // 改进建议输入
  const [suggestionInput, setSuggestionInput] = useState("");
  // 是否正在提交
  const [submitting, setSubmitting] = useState(false);
  // 视图模式：list（卡片列表）/ history（反馈历史）
  const [view, setView] = useState<"list" | "history">("list");
  // 反馈历史查看的卡片 path（null 表示查看全部）
  const [historyFilter, setHistoryFilter] = useState<string | null>(null);

  // ============================================
  // 加载知识卡片列表（递归扫描 Cards 目录）
  // ============================================
  const scanCards = useCallback(async (): Promise<NoteMeta[]> => {
    const result: NoteMeta[] = [];
    const visited = new Set<string>();
    const queue: Array<{ dir: string; depth: number }> = [
      { dir: KNOWLEDGE_CARDS_DIR, depth: 0 },
    ];
    const MAX_DEPTH = 4;
    const MAX_FILES = 500;

    while (queue.length > 0 && result.length < MAX_FILES) {
      const { dir, depth } = queue.shift()!;
      if (visited.has(dir)) continue;
      visited.add(dir);
      try {
        const url = `/vault-api/list?dir=${encodeURIComponent(dir)}&ext=.md`;
        const resp = await fetch(url);
        if (!resp.ok) continue;
        const json = await resp.json();
        if (!json?.success || !Array.isArray(json.files)) continue;
        for (const f of json.files) {
          if (result.length >= MAX_FILES) break;
          if (f.isDirectory) {
            if (depth < MAX_DEPTH) {
              queue.push({ dir: f.path, depth: depth + 1 });
            }
          } else if (f.name && f.name.endsWith(".md")) {
            result.push({
              name: f.name,
              path: f.path,
              mtime: f.mtime || new Date().toISOString(),
              size: f.size || 0,
            });
          }
        }
      } catch {
        // 单目录扫描失败，跳过
      }
    }
    return result;
  }, []);

  const loadNotes = useCallback(async () => {
    setLoading(true);
    try {
      const scanned = await scanCards();
      setNotes(scanned);
    } catch (err) {
      console.warn("[KnowledgeFeedback] 扫描知识卡片失败:", err);
      setNotes([]);
    } finally {
      setLoading(false);
    }
  }, [scanCards]);

  useEffect(() => {
    void loadNotes();
  }, [loadNotes]);

  // 初始加载 localStorage 反馈
  useEffect(() => {
    setFeedbacks(loadFeedbacks());
  }, []);

  // ============================================
  // 反馈汇总（按 path 聚合）
  // ============================================
  const summariesByPath = useMemo(() => {
    const map = new Map<string, FeedbackSummary>();
    for (const fb of feedbacks) {
      let summary = map.get(fb.path);
      if (!summary) {
        summary = {
          path: fb.path,
          title: fb.title,
          usefulCount: 0,
          uselessCount: 0,
          latestVersion: 0,
          latestTimestamp: "",
          history: [],
        };
        map.set(fb.path, summary);
      }
      if (fb.rating === "useful") summary.usefulCount++;
      else summary.uselessCount++;
      if (fb.version > summary.latestVersion) {
        summary.latestVersion = fb.version;
        summary.latestTimestamp = fb.timestamp;
      }
      summary.history.push(fb);
    }
    // 历史按时间倒序
    for (const s of map.values()) {
      s.history.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    }
    return map;
  }, [feedbacks]);

  // ============================================
  // 全局统计
  // ============================================
  const stats = useMemo(() => {
    const total = feedbacks.length;
    const useful = feedbacks.filter((f) => f.rating === "useful").length;
    const useless = feedbacks.filter((f) => f.rating === "useless").length;
    const usefulRate = total > 0 ? Math.round((useful / total) * 100) : 0;
    const uniqueCards = summariesByPath.size;
    return { total, useful, useless, usefulRate, uniqueCards };
  }, [feedbacks, summariesByPath]);

  // ============================================
  // 打开反馈 Modal
  // ============================================
  const openFeedback = useCallback(
    (note: NoteMeta, presetRating?: FeedbackRating) => {
      setActiveNote(note);
      setActiveRating(presetRating || "useful");
      setSceneInput("");
      setSuggestionInput("");
    },
    []
  );

  // 提交反馈
  const handleSubmitFeedback = useCallback(() => {
    if (!activeNote) return;
    setSubmitting(true);
    try {
      const title = activeNote.name.replace(/\.md$/i, "");
      const existing = feedbacks.filter((f) => f.path === activeNote.path);
      const version = existing.length + 1;
      const entry: FeedbackEntry = {
        id: genId(),
        path: activeNote.path,
        title,
        rating: activeRating,
        scene: sceneInput.trim(),
        suggestion: suggestionInput.trim(),
        timestamp: new Date().toISOString(),
        version,
      };
      const next = [...feedbacks, entry];
      setFeedbacks(next);
      saveFeedbacks(next);
      setActiveNote(null);
    } catch (err) {
      console.warn("[KnowledgeFeedback] 提交反馈失败:", err);
    } finally {
      setSubmitting(false);
    }
  }, [activeNote, activeRating, sceneInput, suggestionInput, feedbacks]);

  // 删除单条反馈
  const handleDeleteFeedback = useCallback(
    (id: string) => {
      const next = feedbacks.filter((f) => f.id !== id);
      setFeedbacks(next);
      saveFeedbacks(next);
    },
    [feedbacks]
  );

  // 当前视图的卡片列表（带反馈汇总）
  const notesWithSummary = useMemo(() => {
    return notes.map((note) => {
      const summary = summariesByPath.get(note.path);
      return { note, summary };
    });
  }, [notes, summariesByPath]);

  // 反馈历史视图（按时间倒序）
  const feedbackHistory = useMemo(() => {
    const filtered = historyFilter
      ? feedbacks.filter((f) => f.path === historyFilter)
      : feedbacks;
    return [...filtered].sort(
      (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );
  }, [feedbacks, historyFilter]);

  // ============================================
  // 渲染
  // ============================================
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="relative h-full flex flex-col rounded-xl border border-white/[0.06] bg-gradient-to-br from-[#131826]/60 to-[#0a0e1a]/60 backdrop-blur-md overflow-hidden"
    >
      {/* 顶部光带 */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-rose-500/50 to-transparent" />

      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/[0.04]">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-md flex items-center justify-center bg-rose-500/15 border border-rose-500/30">
            <MessageCircle size={13} className="text-rose-400" />
          </div>
          <div>
            <h3 className="text-[13px] font-semibold text-white tracking-tight">知识使用反馈闭环</h3>
            <p className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mt-0.5">
              Feedback · 👍有用 / 👎没用 · 使用场景 · 改进建议
            </p>
          </div>
        </div>
        <button
          onClick={loadNotes}
          disabled={loading}
          className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-rose-500/15 border border-rose-500/30 hover:bg-rose-500/25 transition-colors disabled:opacity-50"
        >
          {loading ? (
            <Loader2 size={11} className="text-rose-400 animate-spin" />
          ) : (
            <RefreshCw size={11} className="text-rose-400" />
          )}
          <span className="text-[9px] font-mono text-rose-300 uppercase tracking-wider">
            {loading ? "扫描中..." : "刷新"}
          </span>
        </button>
      </div>

      {/* 统计面板 + 视图切换 */}
      <div className="flex items-center gap-2 px-4 py-2 border-b border-white/[0.04] bg-white/[0.01]">
        <div className="flex items-center gap-3 flex-1">
          {/* 统计指标 */}
          <StatBadge label="反馈总数" value={stats.total} color="#F43F5E" />
          <StatBadge label="有用" value={stats.useful} color="#10B981" />
          <StatBadge label="没用" value={stats.useless} color="#F59E0B" />
          <StatBadge label="有用率" value={`${stats.usefulRate}%`} color="#38BDF8" />
          <StatBadge label="覆盖卡片" value={stats.uniqueCards} color="#A78BFA" />
        </div>
        {/* 视图切换 */}
        <div className="flex items-center gap-1 p-0.5 rounded-md border border-white/[0.06] bg-white/[0.02]">
          <button
            onClick={() => setView("list")}
            className={`flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-mono uppercase tracking-wider transition-all ${
              view === "list" ? "bg-rose-500/15 text-rose-300" : "text-gray-500 hover:text-gray-300"
            }`}
          >
            <FileText size={9} />
            卡片列表
          </button>
          <button
            onClick={() => setView("history")}
            className={`flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-mono uppercase tracking-wider transition-all ${
              view === "history" ? "bg-rose-500/15 text-rose-300" : "text-gray-500 hover:text-gray-300"
            }`}
          >
            <History size={9} />
            反馈历史
          </button>
        </div>
      </div>

      {/* 主内容区 */}
      <div className="flex-1 overflow-y-auto p-4">
        {view === "list" ? (
          // 卡片列表视图
          loading ? (
            <div className="h-full flex flex-col items-center justify-center text-gray-500">
              <Loader2 size={20} className="animate-spin text-rose-400 mb-2" />
              <span className="text-[11px] font-mono">正在扫描知识卡片...</span>
            </div>
          ) : notes.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center py-12">
              <FileText size={28} className="text-gray-700 mb-2" />
              <p className="text-[11px] font-mono text-gray-500">
                暂无知识卡片，知识库暂未接入
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2.5">
              {notesWithSummary.map(({ note, summary }, idx) => (
                <FeedbackCard
                  key={`${note.path}-${idx}`}
                  note={note}
                  summary={summary}
                  onFeedback={(rating) => openFeedback(note, rating)}
                  onViewHistory={(path) => {
                    setHistoryFilter(path);
                    setView("history");
                  }}
                />
              ))}
            </div>
          )
        ) : (
          // 反馈历史视图
          <div className="space-y-2">
            {/* 过滤栏 */}
            <div className="flex items-center justify-between px-2 py-1.5 rounded-md border border-white/[0.04] bg-white/[0.02]">
              <div className="flex items-center gap-2">
                <Filter size={11} className="text-rose-400" />
                <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">
                  {historyFilter ? `过滤：${historyFilter}` : "全部反馈"}
                </span>
              </div>
              {historyFilter && (
                <button
                  onClick={() => setHistoryFilter(null)}
                  className="text-[9px] font-mono text-gray-500 hover:text-rose-400"
                >
                  清除过滤
                </button>
              )}
            </div>
            {feedbackHistory.length === 0 ? (
              <div className="text-center py-12 text-[11px] font-mono text-gray-500">
                暂无反馈记录
              </div>
            ) : (
              feedbackHistory.map((fb) => (
                <FeedbackHistoryItem
                  key={fb.id}
                  entry={fb}
                  onDelete={() => handleDeleteFeedback(fb.id)}
                  onFilter={() => {
                    setHistoryFilter(fb.path);
                  }}
                />
              ))
            )}
          </div>
        )}
      </div>

      {/* 底部数据来源说明 */}
      <div className="px-4 py-1.5 border-t border-white/[0.04]">
        <span className="text-[8px] font-mono text-gray-600">
          数据来源：/vault-api/list · localStorage 持久化 · 版本号随反馈累加
        </span>
      </div>

      {/* 反馈输入 Modal */}
      <AnimatePresence>
        {activeNote && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-8"
            onClick={() => setActiveNote(null)}
          >
            <motion.div
              initial={{ scale: 0.95, y: 10 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 10 }}
              transition={{ duration: 0.2 }}
              className="relative w-full max-w-lg rounded-xl border border-rose-500/30 bg-[#0a0e1a] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-5 py-3 border-b border-white/[0.06]">
                <div className="flex items-center gap-2 min-w-0">
                  <MessageCircle size={14} className="text-rose-400 flex-shrink-0" />
                  <h3 className="text-[14px] font-semibold text-white truncate">
                    知识反馈
                  </h3>
                </div>
                <button
                  onClick={() => setActiveNote(null)}
                  className="p-1 rounded-md text-gray-400 hover:text-white hover:bg-white/[0.05] transition-colors flex-shrink-0"
                >
                  <X size={16} />
                </button>
              </div>

              {/* 卡片信息 */}
              <div className="px-5 py-3 border-b border-white/[0.04] bg-white/[0.01]">
                <div className="text-[10px] font-mono text-rose-400 uppercase tracking-wider mb-1">
                  反馈对象
                </div>
                <div className="text-[13px] text-white font-medium mb-1">
                  {activeNote.name.replace(/\.md$/i, "")}
                </div>
                <div className="text-[9px] font-mono text-gray-600 break-all" title={activeNote.path}>
                  📁 {activeNote.path}
                </div>
              </div>

              {/* 反馈内容 */}
              <div className="px-5 py-4 space-y-4">
                {/* 评分选择 */}
                <div>
                  <label className="text-[10px] font-mono text-gray-400 uppercase tracking-wider block mb-2">
                    反馈评分 *
                  </label>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setActiveRating("useful")}
                      className={`flex items-center gap-1.5 px-3 py-2 rounded-md border transition-all ${
                        activeRating === "useful"
                          ? "border-emerald-500/40 bg-emerald-500/15 text-emerald-300"
                          : "border-white/[0.06] bg-white/[0.02] text-gray-400 hover:bg-white/[0.04]"
                      }`}
                    >
                      <ThumbsUp size={12} />
                      <span className="text-[11px] font-mono uppercase tracking-wider">有用</span>
                    </button>
                    <button
                      onClick={() => setActiveRating("useless")}
                      className={`flex items-center gap-1.5 px-3 py-2 rounded-md border transition-all ${
                        activeRating === "useless"
                          ? "border-amber-500/40 bg-amber-500/15 text-amber-300"
                          : "border-white/[0.06] bg-white/[0.02] text-gray-400 hover:bg-white/[0.04]"
                      }`}
                    >
                      <ThumbsDown size={12} />
                      <span className="text-[11px] font-mono uppercase tracking-wider">没用</span>
                    </button>
                  </div>
                </div>

                {/* 使用场景 */}
                <div>
                  <label className="text-[10px] font-mono text-gray-400 uppercase tracking-wider block mb-2">
                    使用场景（在什么情况下使用了这条知识）
                  </label>
                  <textarea
                    value={sceneInput}
                    onChange={(e) => setSceneInput(e.target.value)}
                    placeholder="例如：在客户咨询 XEAOS 知识中心时使用..."
                    rows={2}
                    className="w-full bg-white/[0.02] border border-white/[0.06] rounded-md px-3 py-2 text-[12px] text-gray-200 placeholder-gray-600 outline-none font-mono resize-none focus:border-rose-500/40"
                  />
                </div>

                {/* 改进建议 */}
                <div>
                  <label className="text-[10px] font-mono text-gray-400 uppercase tracking-wider block mb-2">
                    改进建议（可选）
                  </label>
                  <textarea
                    value={suggestionInput}
                    onChange={(e) => setSuggestionInput(e.target.value)}
                    placeholder="例如：建议补充具体示例..."
                    rows={3}
                    className="w-full bg-white/[0.02] border border-white/[0.06] rounded-md px-3 py-2 text-[12px] text-gray-200 placeholder-gray-600 outline-none font-mono resize-none focus:border-rose-500/40"
                  />
                </div>

                {/* 版本提示 */}
                <div className="px-2 py-1.5 rounded-md border border-white/[0.04] bg-white/[0.01]">
                  <span className="text-[9px] font-mono text-gray-500">
                    {(() => {
                      const existing = feedbacks.filter((f) => f.path === activeNote.path);
                      return existing.length > 0
                        ? `该卡片已有 ${existing.length} 条反馈，本次将记录为版本 v${existing.length + 1}`
                        : "首次反馈，将记录为版本 v1";
                    })()}
                  </span>
                </div>
              </div>

              {/* 底部操作 */}
              <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-white/[0.06] bg-white/[0.01]">
                <button
                  onClick={() => setActiveNote(null)}
                  className="px-3 py-1.5 rounded-md border border-white/[0.06] hover:bg-white/[0.04] text-[10px] font-mono text-gray-400 uppercase tracking-wider transition-colors"
                >
                  取消
                </button>
                <button
                  onClick={handleSubmitFeedback}
                  disabled={submitting}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-md bg-rose-500/15 border border-rose-500/30 hover:bg-rose-500/25 transition-colors disabled:opacity-50"
                >
                  {submitting ? (
                    <Loader2 size={11} className="text-rose-400 animate-spin" />
                  ) : (
                    <CheckCircle2 size={11} className="text-rose-400" />
                  )}
                  <span className="text-[10px] font-mono text-rose-300 uppercase tracking-wider">
                    {submitting ? "提交中" : "提交反馈"}
                  </span>
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ============================================
// 子组件：统计徽章
// ============================================
function StatBadge({ label, value, color }: { label: string; value: number | string; color: string }) {
  return (
    <div className="flex items-center gap-1.5 px-2 py-1 rounded-md border border-white/[0.04] bg-white/[0.02]">
      <TrendingUp size={9} style={{ color }} />
      <span className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">{label}</span>
      <span className="text-[11px] font-mono font-semibold" style={{ color }}>
        {value}
      </span>
    </div>
  );
}

// ============================================
// 子组件：反馈卡片
// ============================================
interface FeedbackCardProps {
  note: NoteMeta;
  summary?: FeedbackSummary;
  onFeedback: (rating: FeedbackRating) => void;
  onViewHistory: (path: string) => void;
}

function FeedbackCard({ note, summary, onFeedback, onViewHistory }: FeedbackCardProps) {
  const title = note.name.replace(/\.md$/i, "").replace(/[-_]/g, " ");
  const category = note.path.split("/").slice(-2, -1)[0] || "未分类";
  const hasFeedback = !!summary;
  const totalFeedback = summary ? summary.usefulCount + summary.uselessCount : 0;
  const usefulRate =
    hasFeedback && totalFeedback > 0
      ? Math.round((summary!.usefulCount / totalFeedback) * 100)
      : 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className="relative rounded-lg border border-white/[0.06] bg-white/[0.02] p-3 hover:bg-rose-500/[0.03] hover:border-rose-500/30 transition-colors group"
    >
      {/* 顶部：分类 + 反馈版本 */}
      <div className="flex items-center justify-between mb-2">
        <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-white/[0.04] text-gray-400">
          {category}
        </span>
        {hasFeedback && (
          <span className="text-[8px] font-mono text-rose-400 px-1.5 py-0.5 rounded border border-rose-500/20 bg-rose-500/[0.05]">
            v{summary!.latestVersion}
          </span>
        )}
      </div>

      {/* 标题 */}
      <h4 className="text-[12px] font-medium text-gray-200 line-clamp-2 mb-2 leading-snug">
        {title}
      </h4>

      {/* 路径 */}
      <div className="text-[8px] font-mono text-gray-700 truncate mb-2" title={note.path}>
        {note.path}
      </div>

      {/* 反馈统计 */}
      {hasFeedback && (
        <div className="flex items-center gap-2 mb-2 px-1.5 py-1 rounded-md bg-white/[0.02] border border-white/[0.04]">
          <div className="flex items-center gap-1">
            <ThumbsUp size={9} className="text-emerald-400" />
            <span className="text-[10px] font-mono text-emerald-300">{summary!.usefulCount}</span>
          </div>
          <div className="flex items-center gap-1">
            <ThumbsDown size={9} className="text-amber-400" />
            <span className="text-[10px] font-mono text-amber-300">{summary!.uselessCount}</span>
          </div>
          <span className="text-[9px] font-mono text-gray-500 ml-auto">
            有用率 {usefulRate}%
          </span>
        </div>
      )}

      {/* 底部操作 */}
      <div className="flex items-center justify-between pt-1.5 border-t border-white/[0.04]">
        <div className="flex items-center gap-1">
          <button
            onClick={() => onFeedback("useful")}
            title="有用反馈"
            className="flex items-center gap-1 px-2 py-1 rounded-md border border-emerald-500/20 bg-emerald-500/[0.04] hover:bg-emerald-500/[0.12] transition-colors group/btn"
          >
            <ThumbsUp size={10} className="text-emerald-400" />
            <span className="text-[9px] font-mono text-emerald-300 uppercase">有用</span>
          </button>
          <button
            onClick={() => onFeedback("useless")}
            title="没用反馈"
            className="flex items-center gap-1 px-2 py-1 rounded-md border border-amber-500/20 bg-amber-500/[0.04] hover:bg-amber-500/[0.12] transition-colors group/btn"
          >
            <ThumbsDown size={10} className="text-amber-400" />
            <span className="text-[9px] font-mono text-amber-300 uppercase">没用</span>
          </button>
        </div>
        {hasFeedback && (
          <button
            onClick={() => onViewHistory(note.path)}
            title="查看反馈历史"
            className="flex items-center gap-1 px-1.5 py-1 rounded-md text-gray-500 hover:text-rose-400 hover:bg-white/[0.04] transition-colors"
          >
            <History size={9} />
            <span className="text-[9px] font-mono uppercase">{totalFeedback}</span>
          </button>
        )}
      </div>
    </motion.div>
  );
}

// ============================================
// 子组件：反馈历史项
// ============================================
interface FeedbackHistoryItemProps {
  entry: FeedbackEntry;
  onDelete: () => void;
  onFilter: () => void;
}

function FeedbackHistoryItem({ entry, onDelete, onFilter }: FeedbackHistoryItemProps) {
  const isUseful = entry.rating === "useful";
  return (
    <motion.div
      initial={{ opacity: 0, x: -8 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.2 }}
      className={`rounded-md border p-2.5 ${
        isUseful
          ? "border-emerald-500/20 bg-emerald-500/[0.03]"
          : "border-amber-500/20 bg-amber-500/[0.03]"
      }`}
    >
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-1.5 min-w-0">
          {isUseful ? (
            <ThumbsUp size={11} className="text-emerald-400 flex-shrink-0" />
          ) : (
            <ThumbsDown size={11} className="text-amber-400 flex-shrink-0" />
          )}
          <button
            onClick={onFilter}
            className="text-[11px] text-gray-200 truncate hover:text-rose-300 transition-colors"
            title={entry.path}
          >
            {entry.title}
          </button>
          <span
            className={`flex-shrink-0 px-1.5 py-0.5 rounded text-[8px] font-mono ${
              isUseful
                ? "bg-emerald-500/10 text-emerald-400"
                : "bg-amber-500/10 text-amber-400"
            }`}
          >
            v{entry.version}
          </span>
        </div>
        <button
          onClick={onDelete}
          title="删除反馈"
          className="p-1 rounded text-gray-600 hover:text-red-400 hover:bg-white/[0.04] transition-colors flex-shrink-0"
        >
          <X size={11} />
        </button>
      </div>
      <div className="grid grid-cols-2 gap-2 text-[10px] font-mono">
        <div>
          <span className="text-gray-600">时间：</span>
          <span className="text-gray-400">
            {new Date(entry.timestamp).toLocaleString("zh-CN")}
          </span>
        </div>
      </div>
      {entry.scene && (
        <div className="mt-1 text-[10px] text-gray-400">
          <span className="text-gray-600 font-mono">使用场景：</span>
          {entry.scene}
        </div>
      )}
      {entry.suggestion && (
        <div className="mt-1 text-[10px] text-gray-400">
          <span className="text-gray-600 font-mono">改进建议：</span>
          {entry.suggestion}
        </div>
      )}
      <div className="mt-1 text-[8px] font-mono text-gray-700 truncate" title={entry.path}>
        📁 {entry.path}
      </div>
    </motion.div>
  );
}
